import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkle, Plus } from "@phosphor-icons/react";

const C = { teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B", red: "#F87171" };

/** Compact credit chip + "buy more" link, used in builder/editor headers.
 * Listens for window event `clinq:credits-changed` to refresh after deductions
 * (the PitchEditor dispatches this after every AI draft / export). */
export default function CreditBalance({ variant = "dark" }) {
  const [summary, setSummary] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/credits/me");
      setSummary(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    refresh();
    const onChanged = () => refresh();
    window.addEventListener("clinq:credits-changed", onChanged);
    return () => window.removeEventListener("clinq:credits-changed", onChanged);
  }, [refresh]);

  if (!summary) return null;

  const balance = summary.balance || 0;
  const isLow = balance < 25;          // can't run a full deck export
  const chipColor = isLow ? C.orange : C.tealSoft;
  const dark = variant === "dark";

  return (
    <div className="inline-flex items-center gap-2" data-testid="credit-balance-chip">
      <Link to="/credits"
        title={`${balance} credits available. Click to top up.`}
        className="inline-flex items-center gap-1.5 text-[11px] font-mono-num rounded-full px-2.5 py-1 transition-colors"
        style={{
          backgroundColor: dark ? `${chipColor}15` : `${chipColor}1f`,
          border: `1px solid ${chipColor}55`,
          color: chipColor,
        }}>
        <Sparkle size={10} weight="fill" />
        <span className="font-extrabold tabular-nums">{balance}</span>
        <span className="opacity-80">cr</span>
      </Link>
      <Link to="/credits" data-testid="credit-balance-buy"
        title="Buy credits"
        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-md transition-colors"
        style={{
          border: `1px solid ${dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"}`,
          color: dark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.85)",
        }}>
        <Plus size={9} weight="bold" /> Buy
      </Link>
    </div>
  );
}
